import React, {useEffect, useState} from 'react';
import {View, Text, TouchableOpacity, Image} from 'react-native';
import {useTheme} from 'contexts/ThemeContext';
import styleSheet from './styles';
import enUS from 'date-fns/locale/en-US';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './reactDatepicker.css';
import strings from 'translations/en';
import {useClassStore} from 'contexts/StoreContext';
import {getDayNumberFromFullDate} from 'helpers/getDayNumberFromFullDay';
import {isEqualDates} from 'helpers/compareDates';

interface CalendarViewProps {
  onDayPress?: (date: Date) => void;
  calendarContainer?: object;
  withoutMarkDay?: boolean;
  selected?: Date;
  selectStart?: boolean;
  selectEnd?: boolean;
  startDate?: Date;
  endDate?: Date;
  minDate?: Date;
}

const CalendarView = (props: CalendarViewProps) => {
  const theme = useTheme();
  const styles = styleSheet(theme);

  const classStore = useClassStore();

  const [selected, setSelected] = useState(new Date());

  const firstInitialDate = new Date();

  useEffect(() => {
    if (!props.selected) return;

    setSelected(props.selected);
  }, [props.selected]);

  const CustomHeader = (
    date: Date,
    decreaseMonth: () => void,
    changeMonth: (month: number) => void,
    changeYear: (year: number) => void,
    increaseMonth: () => void,
  ) => {
    const TodayButton = (
      <TouchableOpacity
        style={styles.singleButtonContainer}
        onPress={() => {
          const todayDate = new Date();
          changeMonth(firstInitialDate.getMonth());
          changeYear(firstInitialDate.getFullYear());
          setSelected(todayDate);
          props.onDayPress?.(todayDate);
        }}>
        <Text style={styles.buttonTextStyle}>
          {strings.calendarTitles.webButtonToday}
        </Text>
      </TouchableOpacity>
    );

    return (
      <View style={styles.headerContainer}>
        <View>
          <Text style={styles.textStyle}>
            {date.toLocaleString('default', {month: 'long'})}{' '}
            {date.toLocaleString('default', {year: 'numeric'})}
          </Text>
        </View>
        <View style={styles.buttonsContainer}>
          <TouchableOpacity
            style={styles.singleButtonContainer}
            onPress={() => {
              decreaseMonth();
            }}>
            <Image
              source={theme.images.downArrow}
              style={[styles.imageSize, styles.leftArrow]}
            />
          </TouchableOpacity>
          {TodayButton}
          <TouchableOpacity
            style={styles.singleButtonContainer}
            onPress={() => {
              increaseMonth();
            }}>
            <Image
              source={theme.images.downArrow}
              style={[styles.imageSize, styles.rightArrow]}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.calendarContainer, props.calendarContainer]}>
      <DatePicker
        selected={selected}
        selectsStart={props.selectStart}
        selectsEnd={props.selectEnd}
        startDate={props.startDate}
        endDate={props.endDate}
        minDate={props.minDate}
        onChange={(date: Date) => {
          setSelected(date);
          props.onDayPress?.(date);
        }}
        inline
        locale={enUS}
        calendarStartDay={1}
        formatWeekDay={nameOfDay => nameOfDay.substring(0, 3)}
        renderCustomHeader={({
          date,
          decreaseMonth,
          changeYear,
          changeMonth,
          increaseMonth,
        }) => {
          return CustomHeader(
            date,
            decreaseMonth,
            changeMonth,
            changeYear,
            increaseMonth,
          );
        }}
        renderDayContents={(day, date) => {
          return (
            <View style={styles.dayCell}>
              <Text
                style={
                  date && isEqualDates(selected, date)
                    ? styles.calendarSelectedDayTextStyle
                    : styles.calendarDayTextStyle
                }>
                {day}
              </Text>
              {props.withoutMarkDay
                ? null
                : date && (
                    <>
                      {classStore.hasClassesOnDay(date) && (
                        <View
                          style={[
                            styles.point,
                            isEqualDates(selected, date)
                              ? styles.pointSelected
                              : styles.pointRegular,
                          ]}
                        />
                      )}
                    </>
                  )}
            </View>
          );
        }}
      />
    </View>
  );
};

export default CalendarView;
